import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  Menu as MenuIcon,
  NotificationsOutlined,
  Person,
  Settings,
  Logout,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import { authService, User } from '../../services/authService';
import Sidebar from '../Sidebar/Sidebar';

interface LayoutProps {
  children: React.ReactNode;
  title?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title = 'Dashboard' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const userInfo = await authService.getUserInfo();
        if (userInfo) {
          const newUser: User = {
            id: userInfo.id || '',
            firstName: userInfo.firstname || '',
            lastName: userInfo.lastname || '',
            email: userInfo.email || '',
            premium: userInfo.premium || 'no',
            onboard: userInfo.onboard || 'no',
            createdAt: userInfo.createdAt || new Date().toISOString(),
          };
          setUser(newUser);
        } else {
          const currentUser = authService.getUser();
          if (currentUser) {
            setUser(currentUser);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        const currentUser = authService.getUser();
        if (currentUser) {
          setUser(currentUser);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    handleMenuClose();
  };

  const drawerWidth = 280;

  const userName = user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : t('common.user');
  const userInitials = userName.split(' ').map(n => n[0]).join('').toUpperCase();

  return (
    <Box sx={{ display: 'flex', backgroundColor: androidColors.darkBackground, minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar 
        open={sidebarOpen} 
        onClose={() => setSidebarOpen(false)} 
      />

      {/* Main content */}
      <Box
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${sidebarOpen ? drawerWidth : 0}px)` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* AppBar */}
        <AppBar
          position="static"
          sx={{
            backgroundColor: androidColors.darkBackground,
            boxShadow: 'none',
            borderBottom: `1px solid ${androidColors.borderColor}`,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleSidebarToggle}
              edge="start"
              sx={{
                mr: 2,
                color: androidColors.textColor,
              }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                flexGrow: 1,
                color: androidColors.textColor,
                fontWeight: 'bold',
              }}
            >
              {title}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton sx={{ color: androidColors.textColor }}>
                <NotificationsOutlined />
              </IconButton>
              
              <IconButton onClick={handleMenuClick}>
                {isLoading ? (
                  <Skeleton
                    variant="circular"
                    width={32}
                    height={32}
                    animation="pulse"
                    sx={{
                      bgcolor: `${androidColors.primaryGold}20`,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%': {
                          opacity: 0.6,
                          transform: 'scale(0.95)',
                        },
                        '50%': {
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                        '100%': {
                          opacity: 0.6,
                          transform: 'scale(0.95)',
                        },
                      },
                    }}
                  />
                ) : (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: androidColors.primaryGold,
                    color: androidColors.darkBackground,
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {userInitials}
                </Avatar>
                )}
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{
                  '& .MuiPaper-root': {
                    backgroundColor: androidColors.cardBackground,
                    border: `1px solid ${androidColors.borderColor}`,
                    color: androidColors.textColor,
                    minWidth: 200,
                  },
                }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                    {userName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                    {user?.email}
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: androidColors.borderColor }} />
                <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                  <ListItemIcon>
                    <Person sx={{ color: androidColors.textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('nav.profile')} />
                </MenuItem>
                <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
                  <ListItemIcon>
                    <Settings sx={{ color: androidColors.textColor }} />
                  </ListItemIcon>
                  <ListItemText primary={t('nav.settings')} />
                </MenuItem>
                <Divider sx={{ borderColor: androidColors.borderColor }} />
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout sx={{ color: androidColors.error }} />
                  </ListItemIcon>
                  <ListItemText primary={t('common.logout')} />
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: androidColors.darkBackground,
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;